import React from "react";
import { Box, Button, ButtonBase, Link, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { Campaign, CampaignDetail, getCampaign, getCampaigns } from "../../common/requests/emails/endpoints";
import { Banner, DetailDialog, DetailSection, EmptyState, ErrorState, LoadingRows, Panel, StatusPill, Tone, useResource } from "../../ui";
import EmailPreview from "./EmailPreview";
import BrevoErrorAlert from "./BrevoErrorAlert";
import { errorMessage, formatWhen, isIpBlock, percent } from "./emailData";

const CAMPAIGN_STATUS: Record<string, { label: string; tone: Tone }> = {
  sent: { label: "Sent", tone: "success" },
  draft: { label: "Draft", tone: "neutral" },
  queued: { label: "Scheduled", tone: "info" },
  inProcess: { label: "Sending", tone: "info" },
  suspended: { label: "Suspended", tone: "warning" },
  archive: { label: "Archived", tone: "outline" },
  in_review: { label: "In review", tone: "info" },
};

const campaignStatus = (c: Campaign) => CAMPAIGN_STATUS[c.status] ?? { label: c.status, tone: "neutral" as Tone };

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" component="div" sx={{ color: C.muted, lineHeight: 1.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" className="num" sx={{ fontWeight: 700 }}>
        {value}
        {sub && (
          <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 400, color: C.muted }}>
            {sub}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

function CampaignStatsRow({ c }: { c: Campaign }) {
  const s = c.stats;
  return (
    <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))" } }}>
      <Stat label="Sent" value={String(s.sent)} />
      <Stat label="Delivered" value={percent(s.delivered, s.sent)} sub={`${s.delivered}`} />
      <Stat label="Opened" value={percent(s.opened, s.delivered)} sub={`${s.opened}`} />
      <Stat label="Clicked" value={percent(s.clicked, s.delivered)} sub={`${s.clicked}`} />
      <Stat label="Bounced" value={String(s.bounced)} sub={s.unsubscribed ? `${s.unsubscribed} unsub` : undefined} />
    </Box>
  );
}

function CampaignDialog({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const { idToken } = useAuth();
  const [detail, setDetail] = React.useState<CampaignDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!campaign || !idToken) return;
    let cancelled = false;
    setDetail(null);
    setError(null);
    getCampaign(idToken, campaign.id)
      .then(({ data }) => !cancelled && setDetail(data))
      .catch((err) => !cancelled && setError(errorMessage(err, "Couldn't load the campaign from Brevo.")));
    return () => {
      cancelled = true;
    };
  }, [campaign, idToken]);

  if (!campaign) return null;
  const clicked = (detail?.links ?? []).filter((l) => l.clicks > 0);
  const status = campaignStatus(campaign);

  return (
    <DetailDialog
      open
      onClose={onClose}
      size="lg"
      title={campaign.name}
      subtitle={campaign.subject || "(no subject)"}
      status={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
      actions={
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Typography variant="body2" sx={{ color: C.ink2 }}>
        {campaign.sent_at ? `Sent ${formatWhen(campaign.sent_at)}` : campaign.scheduled_at ? `Scheduled ${formatWhen(campaign.scheduled_at)}` : "Not sent"}
        {campaign.from && ` · from ${campaign.from}`}
        {campaign.list_ids.length > 0 && ` · list ${campaign.list_ids.join(", ")}`}
      </Typography>
      <CampaignStatsRow c={campaign} />
      {clicked.length > 0 && (
        <DetailSection title="Links clicked">
          {clicked.map((l) => (
            <Typography key={l.url} variant="body2" sx={{ display: "flex", gap: 1 }}>
              <Box component="span" className="num" sx={{ fontWeight: 700, minWidth: 28 }}>
                {l.clicks}
              </Box>
              <Link href={l.url} target="_blank" rel="noreferrer" sx={{ overflowWrap: "anywhere", color: C.ink }}>
                {l.url}
              </Link>
            </Typography>
          ))}
        </DetailSection>
      )}
      <DetailSection title="The email">
        {error && <Banner tone="danger">{error}</Banner>}
        {!detail && !error && <LoadingRows rows={3} height={60} />}
        {detail?.html && <EmailPreview html={detail.html} title={campaign.name} />}
        {detail && !detail.html && <Banner>This campaign has no content yet.</Banner>}
      </DetailSection>
    </DetailDialog>
  );
}

/** Marketing campaigns sent from Brevo to the buyer and seller lists. */
export default function Campaigns() {
  const { idToken } = useAuth();
  const [selected, setSelected] = React.useState<Campaign | null>(null);
  const { data: campaigns, error, reload, status } = useResource(
    async () => (await getCampaigns(idToken as string)).data.campaigns,
    [idToken],
    { enabled: !!idToken, errorText: "Couldn't load campaigns from Brevo." }
  );

  if (error && isIpBlock(error)) return <BrevoErrorAlert message={error} onRetry={reload} />;

  return (
    <>
      {error && campaigns && (
        <Banner
          tone="danger"
          action={
            <Button variant="outlined" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Banner>
      )}
      {!campaigns ? (
        <Panel padding={0}>{error && status === "error" ? <ErrorState message={error} onRetry={reload} /> : <LoadingRows rows={3} height={96} />}</Panel>
      ) : campaigns.length === 0 ? (
        <Panel padding={0}>
          <EmptyState title="No campaigns in Brevo yet." />
        </Panel>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {campaigns.map((c) => {
            const st = campaignStatus(c);
            return (
              <ButtonBase
                key={c.id}
                onClick={() => setSelected(c)}
                sx={{
                  display: "block",
                  textAlign: "left",
                  width: "100%",
                  bgcolor: C.paper,
                  border: `1px solid ${C.line}`,
                  borderRadius: "8px",
                  p: { xs: 1.5, sm: 2 },
                  "&:hover": { borderColor: C.line2 },
                  "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: "grid", gap: 1.25 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                        {c.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: C.ink2, overflowWrap: "anywhere" }}>
                        {c.subject || "(no subject)"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                        {c.sent_at ? `Sent ${formatWhen(c.sent_at)}` : c.scheduled_at ? `Scheduled ${formatWhen(c.scheduled_at)}` : `Created ${formatWhen(c.created_at)}`}
                      </Typography>
                    </Box>
                    <StatusPill tone={st.tone}>{st.label}</StatusPill>
                    <ChevronRightIcon sx={{ color: C.faint, mr: -0.5 }} />
                  </Box>
                  {c.status !== "draft" && <CampaignStatsRow c={c} />}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      )}
      <CampaignDialog campaign={selected} onClose={() => setSelected(null)} />
    </>
  );
}
